"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-4 w-16 bg-muted/40 animate-pulse" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse" />
        <div className="h-4 w-16 bg-muted/40 animate-pulse" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-3 lg:gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={`group relative inline-flex h-auto px-0 py-1 font-default text-[11px] font-medium uppercase tracking-[0.08em] text-foreground bg-transparent hover:bg-transparent focus:bg-transparent transition-colors duration-200 ${item.label === "日本語対応" ? "whitespace-nowrap" : ""}`}>
                <span className="relative">
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-secondary transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6 pt-12">
        <div className="h-10 w-3/4 bg-muted/40 animate-pulse" />
        <div className="h-10 w-2/3 bg-muted/40 animate-pulse" />
        <div className="h-10 w-3/4 bg-muted/40 animate-pulse" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-6 pt-12">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="group flex items-baseline gap-4 border-b border-border py-5 transition-colors duration-200 hover:text-secondary">
            <span className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="font-default text-2xl font-light text-foreground transition-colors duration-200 group-hover:text-secondary">
              {item.label}
            </span>
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Mainheader() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="3697" id="mainheader" className={`sticky top-0 left-0 right-0 z-50 w-full bg-background text-foreground transition-all duration-300 ${scrolled ? "bg-background/90 backdrop-blur-md shadow-md border-b border-border/50" : "border-b border-border"}`}>
      <div className="w-full">
        <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between gap-6 px-5 md:px-8 lg:grid lg:grid-cols-[1fr_auto_1fr]">
          <div className="flex items-center justify-start">
            <Link to="/" className="group flex flex-col leading-none">
              <span className="flex items-center">
                <WvcLogo className="h-8 w-auto text-primary" />
              </span>
            </Link>
          </div>

          <div className="hidden lg:flex items-center justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center justify-end gap-5">
            <Link to="#" className="hidden md:inline-flex">
              <Button size="lg" className="h-11 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-[12px] font-semibold uppercase tracking-[0.1em] shadow-sm rounded-md">
                Consultation
              </Button>
            </Link>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden h-11 w-11 text-foreground hover:bg-accent hover:text-accent-foreground border border-border rounded-md" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex h-full flex-col">
                  <div className="flex items-center justify-between border-b border-border px-6 py-6">
                    <div className="flex flex-col leading-none">
                      <WvcLogo className="h-7 w-auto text-primary" />
                      <span className="mt-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-secondary">
                        MORRIE I. LEVINE · COUNSEL
                      </span>
                    </div>
                  </div>

                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer />
                  </MenuProvider>

                  <div className="mt-auto border-t border-border px-6 py-6">
                    <SheetClose asChild>
                      <Link to="#">
                        <Button size="lg" className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-[12px] font-semibold uppercase tracking-[0.1em] shadow-md rounded-md">
                          Request Intake
                        </Button>
                      </Link>
                    </SheetClose>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                        ESTABLISHED FIRM
                      </span>
                      <span className="font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                        SOUTH FLORIDA
                      </span>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

      </div>
    </section>;
}